import { apiClient } from '../lib/apiClient';
import type {
  SubmitMeasurementSessionInput,
  MeasurementSessionResult,
  MeasurementSessionDefaultsResponse,
  TestKitProfileResponse,
} from '../types/measurement';

export interface MeasurementPayload {
  aquariumId: string;
  parameterId: string;
  parameterName: string;
  value: number;
  unit: string;
  measuredAt: string;
}

export interface MeasurementResponse {
  id: string;
  aquariumId: string;
  parameterId: string;
  parameterName: string;
  value: number;
  unit: string;
  measuredAt: string;
  createdAt: string;
  /** 'manual' (hand test or photo), 'icp' (a value of an ICP analysis) or 'controller' (a controller key used as measurement); absent counts as manual. */
  source?: 'manual' | 'icp' | 'controller';
  /** The ICP analysis an 'icp' measurement belongs to. */
  icpAnalysisId?: string | null;
  /** 'YYYY-MM-DD' when the value has no time of day (date-only ICP analysis), else null. */
  measuredDate?: string | null;
}

export async function submitMeasurement(payload: MeasurementPayload): Promise<MeasurementResponse> {
  const response = await apiClient.post<{ data: MeasurementResponse }>(
    `/api/v1/aquariums/${payload.aquariumId}/measurements`,
    payload,
  );
  return response.data;
}

/** The aquarium's measurements — hand tests and ICP values — newest first (GET /aquariums/:aquariumId/measurements, max 500). */
export async function listMeasurements(aquariumId: string): Promise<MeasurementResponse[]> {
  const response = await apiClient.get<{ data: MeasurementResponse[] }>(
    `/api/v1/aquariums/${aquariumId}/measurements`,
  );
  return response.data;
}

// ---------------------------------------------------------------------------
// History ("Verlauf"): measurements + ICP results of one aquarium
// ---------------------------------------------------------------------------

export const HISTORY_RANGES = ['30d', '90d', '1y', 'all'] as const;
export type HistoryRange = (typeof HISTORY_RANGES)[number];

export type HistorySource = 'manual' | 'photo' | 'icp' | 'fauna_marin' | 'controller' | 'device';

export interface HistoryEntry {
  /** Measurement id (kind 'measurement' / 'device') or `${analysisId}:${resultKey}` (kind 'icp'). */
  id: string;
  /** 'icp' and 'device' (imported from a Focustronic Mastertronic or ReefBot) are read-only. */
  kind: 'measurement' | 'icp' | 'device';
  parameterId: string;
  parameterName: string;
  value: number;
  unit: string;
  measuredAt: string;
  source: HistorySource;
  provider: string | null;
  hasEvidencePhoto: boolean;
  analysisId: string | null;
  /** 'YYYY-MM-DD' when the value has no time of day (date-only ICP analysis), else null — show the date only. */
  measuredDate?: string | null;
}

/** A parameter with values in the range — the options of the parameter filter. */
export interface HistoryParameter {
  parameterId: string;
  parameterName: string;
  unit: string;
  count: number;
}

/** At most this many parameters are compared at once (the API rejects more). */
export const MAX_HISTORY_PARAMETERS = 3;

/** Where a target comes from (backend parameterTargetService). */
export type TargetSource = 'default' | 'set_point';

/**
 * Target band of a parameter as the backend resolves it — the personal set
 * point ± tolerance, else the hobby default. The same target drives alerts,
 * the dashboard status / health score and the charts (Reef Guard #1819).
 */
export interface ParameterTarget {
  min: number;
  max: number;
  /** Distance outside [min, max] that still counts as 'stable' (absent from older responses). */
  margin?: number;
  source?: TargetSource;
  setPoint?: number | null;
  tolerance?: number | null;
}

export interface MeasurementHistory {
  range: HistoryRange;
  /** Newest first. */
  entries: HistoryEntry[];
  targets: Record<string, ParameterTarget>;
  truncated: boolean;
  /** The requested parameters (params), or null for all. Absent from older responses. */
  parameterIds?: string[] | null;
  /** Every parameter with values in the range, most values first. Absent from older responses. */
  parameters?: HistoryParameter[];
}

/**
 * GET /aquariums/:aquariumId/measurements/history?range=…[&params=a,b,c]
 * `parameterIds` (1–3) limits the values to those parameters (#1653); without
 * it every parameter of the range is returned.
 */
export async function getMeasurementHistory(
  aquariumId: string,
  range: HistoryRange,
  parameterIds?: readonly string[] | null,
): Promise<MeasurementHistory> {
  const query = new URLSearchParams({ range });
  if (parameterIds && parameterIds.length > 0) query.set('params', parameterIds.join(','));
  const response = await apiClient.get<{ data: MeasurementHistory }>(
    `/api/v1/aquariums/${aquariumId}/measurements/history?${query.toString()}`,
  );
  return response.data;
}

// ---------------------------------------------------------------------------
// Latest value per parameter (1.5.0): dashboard tiles + "Messwert hinzufügen" table
// ---------------------------------------------------------------------------

export interface LatestValue {
  id: string;
  value: number;
  unit: string;
  measuredAt: string;
  /** 'YYYY-MM-DD' for a date-only ICP value (show the date only), else null. */
  measuredDate: string | null;
  source: HistorySource;
}

export type TrendDirection = 'up' | 'down' | 'stable';

export interface ParameterTrend {
  direction: TrendDirection;
  /** latest − previous. */
  delta: number;
}

export interface LatestParameter {
  parameterId: string;
  parameterName: string;
  /** Newest value by measurement time, whatever the source. */
  latest: LatestValue;
  /** The value before it (any source), or null when there is only one. */
  previous: LatestValue | null;
  /** Null without a previous value or when the units cannot be compared. */
  trend: ParameterTrend | null;
  target: ParameterTarget | null;
}

/** GET /aquariums/:aquariumId/measurements/latest — one entry per parameter, sorted by name. */
export async function getLatestMeasurements(aquariumId: string): Promise<LatestParameter[]> {
  const response = await apiClient.get<{ data: { parameters: LatestParameter[] } }>(
    `/api/v1/aquariums/${aquariumId}/measurements/latest`,
  );
  const parameters = response.data?.parameters;
  return Array.isArray(parameters) ? parameters : [];
}

export interface MeasurementUpdate {
  value?: number;
  measuredAt?: string;
}

/** PATCH /aquariums/:aquariumId/measurements/:measurementId — correct value and/or time. */
export async function updateMeasurement(
  aquariumId: string,
  measurementId: string,
  update: MeasurementUpdate,
): Promise<MeasurementResponse> {
  const response = await apiClient.patch<{ data: MeasurementResponse }>(
    `/api/v1/aquariums/${aquariumId}/measurements/${measurementId}`,
    update,
  );
  return response.data;
}

/** DELETE /aquariums/:aquariumId/measurements/:measurementId */
export async function deleteMeasurement(aquariumId: string, measurementId: string): Promise<void> {
  await apiClient.delete(`/api/v1/aquariums/${aquariumId}/measurements/${measurementId}`);
}

/** Short-lived signed URL of a measurement's evidence photo (owner only). */
export async function getEvidencePhotoUrl(aquariumId: string, measurementId: string): Promise<string> {
  const response = await apiClient.get<{ data: { url: string; expiresIn: number } }>(
    `/api/v1/aquariums/${aquariumId}/measurements/${measurementId}/evidence-photo`,
  );
  return response.data.url;
}

export interface PhotoExtractionResult {
  extractionId: string;
  parameter: string | null;
  value: number | null;
  unit: string | null;
  fieldConfidences: Record<string, 'high' | 'medium' | 'low'>;
  deviceModel: string | null;
  deviceModelConfidence: 'high' | 'medium' | 'low' | null;
  warnings: string[];
  /** The private evidence photo the values were read from — sent back on confirm. */
  evidencePhotoKey: string;
}

/**
 * Upload a photo of a test device display for AI-assisted value extraction.
 * This call only extracts candidate values — it never persists a measurement.
 */
export async function extractMeasurementFromPhoto(
  aquariumId: string,
  photo: Blob,
  signal?: AbortSignal,
): Promise<PhotoExtractionResult> {
  const formData = new FormData();
  formData.append('photo', photo, 'measurement-photo.jpg');

  const response = await apiClient.post<{ data: PhotoExtractionResult }>(
    `/api/v1/aquariums/${aquariumId}/measurements/photo-extraction`,
    formData,
    { signal },
  );
  return response.data;
}

export interface ConfirmMeasurementExtractionPayload {
  aquariumId: string;
  parameterId: string;
  parameterName: string;
  value: number;
  unit: string;
  measuredAt: string;
  evidencePhotoKey: string;
  extractionId: string;
}

/**
 * Persist a measurement from a confirmed photo extraction. Only the user-edited
 * values (not the raw AI-extracted values) must be passed in `payload` — this
 * function does not re-read or trust any AI output itself.
 */
export async function confirmMeasurementExtraction(
  payload: ConfirmMeasurementExtractionPayload,
): Promise<MeasurementResponse> {
  const response = await apiClient.post<{ data: MeasurementResponse }>(
    `/api/v1/aquariums/${payload.aquariumId}/measurements`,
    payload,
  );
  return response.data;
}

// ---------------------------------------------------------------------------
// Multi-parameter test session entry (#1740): batch submit + pre-fill aggregate
// ---------------------------------------------------------------------------

/**
 * POST /aquariums/:aquariumId/measurements/session — persist a batch of entries
 * sharing one measuredAt timestamp in a single server-side transaction. The
 * server always generates sessionId; any client-supplied session id is ignored.
 */
export async function submitMeasurementSession(
  aquariumId: string,
  input: SubmitMeasurementSessionInput,
): Promise<MeasurementSessionResult> {
  const response = await apiClient.post<{ data: MeasurementSessionResult }>(
    `/api/v1/aquariums/${aquariumId}/measurements/session`,
    input,
  );
  return response.data;
}

/**
 * GET /aquariums/:aquariumId/measurements/session-defaults — one round trip
 * returning the usual parameters, last value + age, and saved test-kit-profile
 * selections used to pre-fill the Test session form.
 */
export async function fetchMeasurementSessionDefaults(
  aquariumId: string,
): Promise<MeasurementSessionDefaultsResponse> {
  const response = await apiClient.get<{ data: MeasurementSessionDefaultsResponse }>(
    `/api/v1/aquariums/${aquariumId}/measurements/session-defaults`,
  );
  return response.data;
}

/** GET /test-kit-profiles — read-only catalogue of test-kit profiles, no per-user fields. */
export async function fetchTestKitProfiles(): Promise<TestKitProfileResponse[]> {
  const response = await apiClient.get<{ data: TestKitProfileResponse[] }>('/api/v1/test-kit-profiles');
  return response.data;
}

/**
 * PUT …/measurements/test-kit-profile-selections — the tank's default kit for a
 * parameter; with `waitOverrides` the user's own timer lengths for that kit
 * (guided test session), keyed by step index.
 */
export async function saveTestKitSelection(
  aquariumId: string,
  parameterId: string,
  testKitProfileId: string,
  waitOverrides?: Record<string, number> | null,
): Promise<void> {
  await apiClient.put(`/api/v1/aquariums/${encodeURIComponent(aquariumId)}/measurements/test-kit-profile-selections`, {
    parameterId,
    testKitProfileId,
    ...(waitOverrides !== undefined ? { waitOverrides } : {}),
  });
}
